import type { UVTransform } from "@visorxr/core";

export interface Point2 {
  x: number;
  y: number;
}

/** Overlay corners in the order the tracker emits them: top-left, top-right, bottom-right, bottom-left. */
export type Quad = readonly [Point2, Point2, Point2, Point2];

export interface FrameSize {
  width: number;
  height: number;
}

/** No crop — the frame is assumed to fill clip space edge to edge. */
const IDENTITY_COVER_FIT: UVTransform = { repeat: { x: 1, y: 1 }, offset: { x: 0, y: 0 } };

/**
 * Frame pixels to clip space.
 *
 * The vertical flip is the part worth stating: frame pixels count down from the top, clip space
 * counts up from the centre. Miss it and the overlay renders upside down while every intermediate
 * number still looks entirely reasonable.
 *
 * `coverFit` is the same repeat/offset transform the background feed is cropped by
 * (`ARScene.getBackgroundCoverFit`). The tracker's frame and the background video share one
 * camera feed at the same aspect (`processingSize` only scales, never crops), so a frame-pixel
 * fraction and a background-texture UV fraction address the same physical point — but the
 * background only shows the fraction range `[offset, offset + repeat]` of it. Mapping frame pixels
 * straight to clip space while ignoring that crop is exactly what leaves the overlay sitting offset
 * from the background it is meant to be glued to; composing the crop here is what fixes it. Omit it
 * (or pass identity) when no cover-fit is known yet — equivalent to assuming no crop.
 *
 * The y-axis term assumes a **centred** crop (`offset.y === (1 - repeat.y) / 2`), which is all
 * `coverFitUVTransform` — the sole producer of this value — ever emits; a texture's vertical
 * flip makes an off-centre `offset.y` ambiguous without knowing which edge it crops from, and
 * nothing here resolves that. The x-axis term has no such restriction.
 */
export function quadToClipSpace(quad: Quad, frame: FrameSize, coverFit: UVTransform = IDENTITY_COVER_FIT): Quad {
  return quad.map((corner) => {
    const fractionX = corner.x / frame.width;
    const fractionY = corner.y / frame.height;
    return {
      x: -1 + (2 * (fractionX - coverFit.offset.x)) / coverFit.repeat.x,
      y: 1 - (2 * (fractionY - coverFit.offset.y)) / coverFit.repeat.y,
    };
  }) as unknown as Quad;
}

/**
 * Per-corner UV weights that make texture interpolation projective.
 *
 * A GPU interpolates across two triangles affinely. For a quad under perspective that is wrong: the
 * texture creases visibly along the shared diagonal, which on a poster overlay reads as the video
 * bending. Dividing each corner's UV by its distance ratio along the diagonals — the classic
 * homogeneous `q` coordinate — restores the projective mapping the warp actually describes.
 *
 * Returns all-ones for a degenerate quad, so a collapsed track renders flat rather than dividing by
 * zero; the tracker's own degeneracy checks are what should have caught it first.
 */
export function projectiveUvWeights(quad: Quad): [number, number, number, number] {
  const [topLeft, topRight, bottomRight, bottomLeft] = quad;

  const intersection = diagonalIntersection(topLeft, bottomRight, topRight, bottomLeft);
  if (!intersection) return [1, 1, 1, 1];

  const topLeftToCentre = distance(topLeft, intersection);
  const bottomRightToCentre = distance(bottomRight, intersection);
  const topRightToCentre = distance(topRight, intersection);
  const bottomLeftToCentre = distance(bottomLeft, intersection);

  if (topLeftToCentre === 0 || topRightToCentre === 0 || bottomRightToCentre === 0 || bottomLeftToCentre === 0) {
    return [1, 1, 1, 1];
  }

  return [
    (topLeftToCentre + bottomRightToCentre) / bottomRightToCentre,
    (topRightToCentre + bottomLeftToCentre) / bottomLeftToCentre,
    (bottomRightToCentre + topLeftToCentre) / topLeftToCentre,
    (bottomLeftToCentre + topRightToCentre) / topRightToCentre,
  ];
}

/** Where the quad's two diagonals cross; null when they are parallel or the quad has collapsed. */
function diagonalIntersection(a1: Point2, a2: Point2, b1: Point2, b2: Point2): Point2 | null {
  const aDeltaX = a2.x - a1.x;
  const aDeltaY = a2.y - a1.y;
  const bDeltaX = b2.x - b1.x;
  const bDeltaY = b2.y - b1.y;

  const denominator = aDeltaX * bDeltaY - aDeltaY * bDeltaX;
  if (Math.abs(denominator) < 1e-9) return null;

  const t = ((b1.x - a1.x) * bDeltaY - (b1.y - a1.y) * bDeltaX) / denominator;
  return { x: a1.x + t * aDeltaX, y: a1.y + t * aDeltaY };
}

function distance(a: Point2, b: Point2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
